import { AxiosResponse } from 'axios';
import type { Chart } from 'highcharts';
import logo from '../assets/logos/logo-full-dark.png';

function filenameFromContentDisposition(
  cd?: string | null
): string | undefined {
  if (!cd) return;
  // Handle RFC 6266 / RFC 5987 (filename*), then plain filename=
  // e.g. attachment; filename*=UTF-8''my%20file.txt; filename="my file.txt"
  const star = cd.match(/filename\*\s*=\s*([^;]+)/i);
  if (star) {
    const v = star[1].trim().replace(/^UTF-8''/i, '');
    try {
      return decodeURIComponent(v.replace(/^"+|"+$/g, ''));
    } catch {
      return v;
    }
  }
  const simple = cd.match(/filename\s*=\s*("?)([^";]+)\1/i);
  if (simple) return simple[2];
}

function filenameFromUrl(url?: string): string | undefined {
  if (!url) return;
  try {
    const u = new URL(url);
    // If backend set ResponseContentDisposition on the presigned URL, it will appear here:
    const rcd = u.searchParams.get('response-content-disposition');
    const fromRcd = filenameFromContentDisposition(rcd);
    if (fromRcd) return fromRcd;

    // Otherwise, fallback to the key name (last path segment)
    const path = decodeURIComponent(u.pathname);
    const segs = path.split('/').filter(Boolean);
    const last = segs[segs.length - 1];
    if (last) return last;
  } catch {
    // If URL constructor fails, try a naive split:
    const parts = url.split('?')[0].split('/');
    return decodeURIComponent(parts[parts.length - 1] || 'download');
  }
}

function inferFilename(resp: AxiosResponse, fallback?: string): string {
  // 1) Try Content-Disposition (if CORS ExposeHeaders includes it)
  const cdHeader =
    resp.headers?.['content-disposition'] ??
    resp.headers?.['Content-Disposition'];
  const viaHeader = filenameFromContentDisposition(cdHeader);
  if (viaHeader) return viaHeader;

  // 2) Try the final URL axios/XHR resolved to
  //    In browsers, axios exposes XHR.responseURL at response.request.responseURL
  const finalUrl: string | undefined =
    (resp.request && resp.request.responseURL) || undefined;
  const viaUrl = filenameFromUrl(finalUrl);
  if (viaUrl) return viaUrl;

  // 3) Fallback
  return fallback || 'download';
}

export function handleStreamingDownload(
  response: AxiosResponse,
  fallbackFilename: string = 'download'
): void {
  if (!response.data) {
    console.warn('No data in streaming response');
    return;
  }

  // Get filename from Content-Disposition header
  let filename = inferFilename(response, fallbackFilename);

  const contentType =
    (response.headers['content-type'] as string | undefined) ||
    'application/octet-stream';

  // Create blob and trigger download
  const blob = new Blob([response.data], { type: contentType });
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);

  console.log('File downloaded successfully:', filename);
}

/**
 * Creates functions for adding and removing a logo from a Highcharts chart
 * Used for custom export/print functionality
 * @param logoHeight - The calculated height of the logo based on aspect ratio
 * @returns Object with addLogo and removeLogo functions, plus a ref to track the logo element
 */
export function createLogoHandlers(logoHeight: number | null) {
  let chartLogo: Highcharts.SVGElement | null = null;
  const logoWidth = 200;

  const addLogo = function (this: Chart) {
    if (logoHeight === null) return;
    const chartHeight = this.chartHeight;
    const logoY = chartHeight - logoHeight - 15;

    chartLogo = this.renderer
      .image(logo, 15, logoY, logoWidth, logoHeight)
      .add();
  };
  const removeLogo = function (this: Chart) {
    if (logoHeight === null) return;

    if (chartLogo !== null) {
      chartLogo.destroy();
      chartLogo = null;
    }
  };

  return { addLogo, removeLogo, chartLogo };
}

/**
 * Calculates the logo height based on the logo image's aspect ratio
 * @param logoWidth - The desired width of the logo (default: 100)
 * @returns Promise that resolves with the calculated logo height
 */
export function calculateLogoHeight(logoWidth: number = 200): Promise<number> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const logoHeight = (logoWidth / img.naturalWidth) * img.naturalHeight;
      resolve(logoHeight);
    };
    img.onerror = () => {
      reject(new Error('Failed to load logo image'));
    };
  });
}

/**
 * Creates a standardized Highcharts exporting configuration with custom title and logo support
 * @param options - Configuration options for the export
 * @returns Highcharts exporting configuration object
 */
export interface CreateExportConfigOptions {
  filename?: string;
  logoHeight: number | null;
  addLogo: (this: Chart) => void;
  removeLogo: (this: Chart) => void;
  downloadIconUri?: string;
  fullscreenIconUri?: string;
  fullscreenExitIconUri?: string;
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
  customTitle?: string;
  includeFullscreenButton?: boolean;
}

export function createExportConfig({
  filename,
  logoHeight,
  addLogo,
  downloadIconUri,
  fullscreenIconUri,
  fullscreenExitIconUri,
  onFullscreenToggle,
  isFullscreen = false,
  customTitle,
  includeFullscreenButton = true,
}: CreateExportConfigOptions): Highcharts.ExportingOptions {
  const buttons: Record<string, Highcharts.ExportingButtonsOptionsObject> = {};

  // Add fullscreen button if enabled
  if (
    includeFullscreenButton &&
    fullscreenIconUri &&
    fullscreenExitIconUri &&
    onFullscreenToggle
  ) {
    buttons.fullscreen = {
      symbol: `url(${
        isFullscreen ? fullscreenExitIconUri : fullscreenIconUri
      })`,
      symbolSize: 25,
      symbolX: 28.25,
      symbolY: 28.5,
      onclick: onFullscreenToggle,
    };
  }

  // Add download button
  if (downloadIconUri) {
    buttons.download = {
      symbol: `url(${downloadIconUri})`,
      symbolSize: 25,
      symbolX: 25.25,
      symbolY: 25.5,
      x: 0,
      y: includeFullscreenButton ? 25 : 0,
      menuItems: [
        'viewFullscreen',
        'printChart',
        'separator',
        'downloadPNG',
        'downloadJPEG',
        'downloadSVG',
      ],
    };
  }

  const totalMarginBottom = logoHeight !== null ? logoHeight + 80 : 40;

  return {
    buttons: Object.keys(buttons).length > 0 ? buttons : undefined,
    filename: filename || 'chart',
    sourceWidth: 1600,
    sourceHeight: 1200,
    scale: 2,
    chartOptions: {
      chart: {
        events: {
          load: addLogo,
        },
        // Proper margins for all sides in exports
        marginTop: 50,
        marginRight: 150,
        marginBottom: totalMarginBottom,
        marginLeft: 200,
      },
      title: customTitle ? { text: customTitle } : undefined,
    },
    fallbackToExportServer: false,
  };
}
